import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';

import eventsToIconsMap, { GUARD_REVIEWED_EVENT_TYPE } from '$src/pages/EventSummary/EventsToIconsMap';
import useStores from './useStores';

// Helper method to reduce fetched events
const reduceEventResults = (fetchedEventsPromises, userIds) => fetchedEventsPromises.reduce((acc, events, index) => ({
  items: [...acc.items, ...(events?.items ?? [])],
  count: acc.count + (events?.count || 0),
  nextToken: {
    ...acc.nextToken,
    ...(events?.nextToken ? { [userIds[index]]: events?.nextToken } : {})
  }
}), { items: [], count: 0, nextToken: {} });

function useEventsAndLocations(userIds, locationId, locations) {
  const {
    eventStore: { fetchEvents },
  } = useStores();
  const [loading, setLoading] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [events, setEvents] = useState();
  const [emergencies, setEmergencies] = useState();
  const [eventResolutionTypes, setEventResolutionTypes] = useState();
  const [emergencyResolutionTypes, setEmergencyResolutionTypes] = useState();

  const LAST_60_DAYS = moment().subtract(60, 'days').startOf('day').unix();
  const LIMIT = 100;

  /*
   * Fetch events for the specific resolution types from the event history API with special handling
   * for GuardReviewed events. There are two distinct cases:
   *
   * 1. Standard events: For most resolution types, fetch events directly using the type as a filter
   * 2. GuardReviewed events: These represent miscellaneous events that don't fall into any of our
   *    predefined categories. When this type is requested, fetch all events first, then filter out
   *    any events that belong to our predefined categories.
   */
  const fetchEventsForResolutionTypes = async (targetUserIds, targetLocationId, startTime, limit, nextToken) => {
    // If it's a guard reviewed event type, don't send resolution types to the API to fetch all events
    const isGuardReviewedEvent = eventResolutionTypes && eventResolutionTypes.includes(GUARD_REVIEWED_EVENT_TYPE);
    const resolutionTypesQueryParam = isGuardReviewedEvent ? null : eventResolutionTypes;
    const fetchedEventsPromises = await Promise.all(targetUserIds.map((userId) => fetchEvents(userId, targetLocationId, startTime, limit, nextToken ? nextToken[userId] : null, resolutionTypesQueryParam)));
    const fetchedEvents = reduceEventResults(fetchedEventsPromises, targetUserIds);


    if (!isGuardReviewedEvent) {
      return fetchedEvents;
    }

    // If we are looking for guard reviewed events, filter events to only include those whose resolution_type is
    // NOT in the eventsToIconsMap. These are the events that don't have specific icon mappings that we use GuardReviewed
    // as the catch-all for
    const eventsWithIcons = Object.keys(eventsToIconsMap);
    const guardReviewedEvents = fetchedEvents?.items?.filter((event) => !eventsWithIcons.includes(event.resolution_type)) ?? [];
    const filteredEvents = fetchedEvents?.items?.filter((event) => eventResolutionTypes.includes(event.resolution_type)) ?? [];

    return {
      items: [...guardReviewedEvents, ...filteredEvents],
      count: guardReviewedEvents.length + filteredEvents.length,
      nextToken: fetchedEvents?.nextToken,
    };
  };

  const fetchEmergenciesForResolutionTypes = async (targetUserIds, targetLocationId, startTime, limit, nextToken) => {
    const defaultEmergencyResolutionTypes = Object.keys(eventsToIconsMap).filter((eventKey) => eventsToIconsMap[eventKey].isEmergency);
    const resolutionTypesQueryParam = emergencyResolutionTypes || defaultEmergencyResolutionTypes;

    const fetchedEmergenciesPromises = await Promise.all(targetUserIds.map((userId) => fetchEvents(userId, targetLocationId, startTime, limit, nextToken ? nextToken[userId] : null, resolutionTypesQueryParam)));
    const fetchedEmergencies = reduceEventResults(fetchedEmergenciesPromises, targetUserIds);;

    return fetchedEmergencies;
  };

  useEffect(() => {
    const getEvents = async () => {
      if (!loading) {
        setLoading(true);
        setEvents(await fetchEventsForResolutionTypes(userIds, locationId, LAST_60_DAYS, LIMIT, null));
        setLoading(false);
      }
    };

    getEvents();
  }, [userIds, locationId, fetchEvents, eventResolutionTypes]);

  useEffect(() => {
    const getEmergencies = async () => {
      if (!loading) {
        setLoading(true);
        setEmergencies(await fetchEmergenciesForResolutionTypes(userIds, locationId, LAST_60_DAYS, LIMIT, null));
        setLoading(false);
      }
    };

    getEmergencies();
  }, [userIds, locationId, fetchEvents, emergencyResolutionTypes]);

  const eventsLocations = useMemo(() => {
    if (!events || !locations) {
      return [];
    }

    const locationMap = new Map(locations.map((location) => [location.locationId, location]));

    return events.items?.map((event) => ({
      ...event,
      ...locationMap.get(event.location_id),
    }));
  }, [events, locations]);

  const emergenciesLocations = useMemo(() => {
    if (!emergencies || !locations) {
      return [];
    }

    const locationMap = new Map(locations.map((location) => [location.locationId, location]));

    return emergencies.items?.map((emergency) => ({
      ...emergency,
      ...locationMap.get(emergency.location_id),
    }));
  }, [emergencies, locations]);

  const fetchNextPageOfEvents = async (userIds, locationId, nextToken) => {
    if (!loadingNextPage) {
      setLoadingNextPage(true);
      const newEvents = await fetchEventsForResolutionTypes(userIds, locationId, LAST_60_DAYS, LIMIT, nextToken);

      if (newEvents && newEvents.count > 0) {
        const currentItems = events?.items ?? [];
        const currentCount = events?.count ?? 0;

        setEvents({
          items: [...currentItems, ...newEvents.items],
          count: currentCount + newEvents.count,
          nextToken: newEvents.nextToken ?? null,
        });
      }

      setLoadingNextPage(false);
    }
  };

  const fetchNextPageOfEmergencies = async (userIds, locationId, nextToken) => {
    if (!loadingNextPage) {
      setLoadingNextPage(true);
      const newEvents = await fetchEmergenciesForResolutionTypes(userIds, locationId, LAST_60_DAYS, LIMIT, nextToken);

      if (newEvents && newEvents.count > 0) {
        const currentItems = emergencies?.items ?? [];
        const currentCount = emergencies?.count ?? 0;

        setEmergencies({
          items: [...currentItems, ...newEvents.items],
          count: currentCount + newEvents.count,
          nextToken: newEvents.nextToken ?? null,
        });
      }

      setLoadingNextPage(false);
    }
  };

  return {
    eventsLocations: {
      items: eventsLocations,
      count: events?.count,
      nextToken: events?.nextToken,
    },
    emergenciesLocations: {
      items: emergenciesLocations,
      count: emergencies?.count,
      nextToken: emergencies?.nextToken,
    },
    fetchNextPageOfEvents,
    fetchNextPageOfEmergencies,
    loadingEventsLocations: loading,
    filterEventsByResolutionTypes: (eventTypes) => setEventResolutionTypes(eventTypes[0] ? eventTypes.join() : null),
    filterEmergenciesByResolutionTypes: (emergencyTypes) => setEmergencyResolutionTypes(emergencyTypes[0] ? emergencyTypes.join() : null)
  };
}

export default useEventsAndLocations;
